import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import { formatearNumeroCL } from '../utils/formatUtils';
import { showConfirm } from '../lib/swal';
import EnvioEstadoStepper from '../components/EnvioEstadoStepper';
import TablaScroll from '../components/TablaScroll';

export default function HistorialEnvios() {
  const user = useAuthStore(state => state.user);
  const [historial, setHistorial] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [toners, setToners] = useState([]);
  const [tiposToner, setTiposToner] = useState([]);
  
  // Filtros
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroToner, setFiltroToner] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    // Cargar historial completo
    const { data: historialData, error: errorHistorial } = await supabase
      .from('envios_cliente_historial')
      .select(`
        *,
        clientes(nombre),
        toners(modelo, rendimiento),
        tipos_toner(nombre)
      `)
      .order('creado_el', { ascending: false });

    if (!errorHistorial) {
      // Cargar guías de despacho desde envios_cliente_aprobados para registros que no tienen guía en historial
      const { data: aprobadosData } = await supabase
        .from('envios_cliente_aprobados')
        .select('cliente_id, toner_id, tipo_toner_id, mes_correspondiente, cantidad_despachada, guia')
        .not('guia', 'is', null);

      // Crear un mapa de guías por cliente/tóner/tipo/mes/cantidad para mayor precisión
      const guiasMap = {};
      (aprobadosData || []).forEach(item => {
        const key = `${item.cliente_id}-${item.toner_id}-${item.tipo_toner_id}-${item.mes_correspondiente}-${item.cantidad_despachada}`;
        guiasMap[key] = item.guia;
      });

      // Procesar historial con múltiples fuentes de guías
      const historialConGuias = (historialData || []).map(envio => {
        let guia = null;
        
        // 1. PRIORIDAD: Si las observaciones contienen "GUÍA:" extraer de ahí (para despachos de pendientes)
        if (envio.observaciones && envio.observaciones.includes('GUÍA:')) {
          const guiaMatch = envio.observaciones.match(/GUÍA:\s*([^\s\|]+)/i);
          if (guiaMatch) {
            guia = guiaMatch[1].trim();
          }
        }
        
        // 2. FALLBACK: Si no se encontró en observaciones, buscar en envios_cliente_aprobados
        if (!guia) {
          const key = `${envio.cliente_id}-${envio.toner_id}-${envio.tipo_toner_id}-${envio.mes_correspondiente}-${envio.cantidad_despachada}`;
          guia = guiasMap[key] || null;
        }
        
        return {
          ...envio,
          guia: guia
        };
      });

      setHistorial(historialConGuias);
    }

    // Cargar datos para filtros
    const { data: clientesData } = await supabase
      .from('clientes')
      .select('*')
      .order('nombre');
    setClientes(clientesData || []);

    const { data: tonersData } = await supabase
      .from('toners')
      .select('*')
      .order('modelo');
    setToners(tonersData || []);

    const { data: tiposData } = await supabase
      .from('tipos_toner')
      .select('*')
      .order('nombre');
    setTiposToner(tiposData || []);
  };

  const eliminarRegistro = async (id) => {
    const confirmado = await showConfirm(
      '¿Eliminar este registro del historial?',
      'Esta acción no se puede deshacer.',
      { peligro: true }
    );
    if (!confirmado) {
      return;
    }

    const { error } = await supabase
      .from('envios_cliente_historial')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error al eliminar registro: ' + error.message);
      return;
    }

    toast.success('Registro eliminado correctamente');
    cargarDatos();
  };

  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr + 'T00:00:00');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[fecha.getMonth()]} ${fecha.getFullYear()}`;
  };

  // Obtener meses únicos del historial
  const mesesUnicos = [...new Set(historial.map(envio => formatearFecha(envio.mes_correspondiente)))].sort();

  // Filtrar clientes para búsqueda
  const clientesFiltrados = clientes.filter(cliente =>
    cliente.nombre.toLowerCase().includes(filtroCliente.toLowerCase())
  );

  // Aplicar filtros
  const terminoBusqueda = busqueda.trim().toLowerCase();
  const historialFiltrado = historial.filter(envio => {
    // Búsqueda global: revisa cliente, tóner, tipo, mes y guía a la vez
    const coincideBusqueda = terminoBusqueda
      ? (
          (envio.clientes?.nombre || '').toLowerCase().includes(terminoBusqueda) ||
          (envio.toners?.modelo || '').toLowerCase().includes(terminoBusqueda) ||
          (envio.tipos_toner?.nombre || '').toLowerCase().includes(terminoBusqueda) ||
          formatearFecha(envio.mes_correspondiente).toLowerCase().includes(terminoBusqueda) ||
          (envio.guia || '').toLowerCase().includes(terminoBusqueda)
        )
      : true;

    const cumpleFiltroCliente = !filtroCliente || 
      envio.clientes?.nombre.toLowerCase().includes(filtroCliente.toLowerCase());
    
    const cumpleFiltroToner = !filtroToner || 
      envio.toners?.modelo.toLowerCase().includes(filtroToner.toLowerCase());
    
    const cumpleFiltroTipo = !filtroTipo || 
      envio.tipos_toner?.nombre.toLowerCase().includes(filtroTipo.toLowerCase());
    
    const cumpleFiltroMes = !filtroMes || 
      formatearFecha(envio.mes_correspondiente).toLowerCase().includes(filtroMes.toLowerCase());
    
    const cumpleFiltroEstado = !filtroEstado || 
      (filtroEstado === 'aprobado' && envio.resultado === 'aprobado') ||
      (filtroEstado === 'rechazado' && envio.resultado === 'rechazado');

    const fechaCreacion = new Date(envio.creado_el).toISOString().split('T')[0];
    const cumpleFiltroFechaDesde = !filtroFechaDesde || fechaCreacion >= filtroFechaDesde;
    const cumpleFiltroFechaHasta = !filtroFechaHasta || fechaCreacion <= filtroFechaHasta;

    return coincideBusqueda && cumpleFiltroCliente && cumpleFiltroToner && cumpleFiltroTipo && 
           cumpleFiltroMes && cumpleFiltroEstado && cumpleFiltroFechaDesde && cumpleFiltroFechaHasta;
  });

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historialFiltrado.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(historialFiltrado.length / itemsPerPage);

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroCliente('');
    setFiltroToner('');
    setFiltroTipo('');
    setFiltroMes('');
    setFiltroEstado('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setCurrentPage(1);
  };

  const hayFiltrosAvanzadosActivos = filtroCliente || filtroToner || filtroTipo || filtroMes || filtroEstado || filtroFechaDesde || filtroFechaHasta;

  return (
    <div className="p-4 sm:p-6 pt-16 lg:pt-6 ml-0 lg:ml-72 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gradient">Historial Completo de Envíos</span>
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Todos los registros y movimientos de envíos por cliente
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={limpiarFiltros}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 text-white font-medium rounded-xl hover:bg-gray-600 hover:shadow-lg transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Limpiar Filtros
            </button>
            <button
              onClick={cargarDatos}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Buscador principal */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            placeholder="Buscar por cliente, tóner, tipo, mes o guía..."
            className="w-full pl-12 pr-10 py-3.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white text-base"
          />
          {busqueda && (
            <button
              onClick={() => { setBusqueda(''); setCurrentPage(1); }}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              title="Limpiar búsqueda"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 px-1">
          <span className="text-sm text-gray-500">
            {historialFiltrado.length} {historialFiltrado.length === 1 ? 'registro' : 'registros'}
            {(terminoBusqueda || hayFiltrosAvanzadosActivos) ? ' encontrados' : ' en total'}
          </span>
          <button
            onClick={() => setMostrarFiltrosAvanzados(v => !v)}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {mostrarFiltrosAvanzados ? 'Ocultar filtros avanzados' : 'Filtros avanzados'}
            {hayFiltrosAvanzadosActivos && !mostrarFiltrosAvanzados && (
              <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filtros */}
      {mostrarFiltrosAvanzados && (
      <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-sky-100 to-blue-100 rounded-lg">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900">Filtros de Búsqueda</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
            <input
              type="text"
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              placeholder="Buscar cliente..."
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tóner</label>
            <select
              value={filtroToner}
              onChange={(e) => setFiltroToner(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">Todos los tóners</option>
              {toners.map(toner => (
                <option key={toner.id} value={toner.modelo}>{toner.modelo}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Tóner</label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">Todos los tipos</option>
              {tiposToner.map(tipo => (
                <option key={tipo.id} value={tipo.nombre}>{tipo.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">Todos los estados</option>
              <option value="aprobado">Aprobado</option>
              <option value="rechazado">Rechazado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mes</label>
            <select
              value={filtroMes}
              onChange={(e) => setFiltroMes(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">Todos los meses</option>
              {mesesUnicos.map(mes => (
                <option key={mes} value={mes}>{mes}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Desde</label>
            <input
              type="date"
              value={filtroFechaDesde}
              onChange={(e) => setFiltroFechaDesde(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Hasta</label>
            <input
              type="date"
              value={filtroFechaHasta}
              onChange={(e) => setFiltroFechaHasta(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="text-sm text-gray-600">
          Mostrando {historialFiltrado.length} de {historial.length} registros
        </div>
      </div>
      )}

      {/* Tabla de Historial */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-sky-100 to-blue-100 rounded-lg">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Historial de Movimientos</h3>
            </div>
            <span className="bg-gradient-to-r from-sky-100 to-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-bold border border-blue-200">
              {historialFiltrado.length} registro(s)
            </span>
          </div>
        </div>

        {historialFiltrado.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-500 text-lg font-medium">No hay registros que coincidan con los filtros</p>
          </div>
        ) : (
          <>
            <TablaScroll>
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2">Cliente</th>
                    <th className="px-4 py-2">Tóner</th>
                    <th className="px-4 py-2">Tipo</th>
                    <th className="px-4 py-2">Mes</th>
                    <th className="px-4 py-2 text-center">Día Envío</th>
                    <th className="px-4 py-2">Copias</th>
                    <th className="px-4 py-2">Cantidad</th>
                    <th className="px-4 py-2">Tipo Movimiento</th>
                    <th className="px-4 py-2">Estado</th>
                    <th className="px-4 py-2">Guía</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((envio) => {
                    // Extraer día de envío de las observaciones
                    const diaEnvioMatch = envio.observaciones?.match(/📅 DÍA DE ENVÍO:\s*(\w+)/);
                    const diaEnvio = diaEnvioMatch ? diaEnvioMatch[1] : null;

                    return (
                      <tr key={envio.id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {new Date(envio.creado_el).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 font-medium">{envio.clientes?.nombre}</td>
                        <td className="px-4 py-2">{envio.toners?.modelo}</td>
                        <td className="px-4 py-2">{envio.tipos_toner?.nombre}</td>
                        <td className="px-4 py-2">{formatearFecha(envio.mes_correspondiente)}</td>
                        <td className="px-4 py-2 text-center">
                          {diaEnvio ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200">
                              📅 {diaEnvio}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2">{formatearNumeroCL(envio.copias_realizadas)}</td>
                        <td className="px-4 py-2 font-bold text-blue-700">{envio.cantidad_despachada}</td>
                        <td className="px-4 py-2">
                          {envio.copias_realizadas > 0 ? (
                            <span className="text-xs font-semibold px-2 py-1 rounded bg-blue-100 text-blue-700">
                              📊 Registro Mensual
                            </span>
                          ) : envio.observaciones?.includes('ENVÍO POR ADELANTADO') ? (
                            <span className="text-xs font-semibold px-2 py-1 rounded bg-purple-100 text-purple-700">
                              🚀 Envío Adelantado
                            </span>
                          ) : (
                            <span className="text-xs font-semibold px-2 py-1 rounded bg-orange-100 text-orange-700">
                              🚚 Despacho Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {envio.resultado === 'rechazado' ? (
                            <EnvioEstadoStepper estado="rechazado" compacto />
                          ) : (
                            <EnvioEstadoStepper
                              estado={envio.resultado || 'aprobado'}
                              tieneGuia={!!envio.guia}
                              compacto
                            />
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {envio.guia ? (
                            <span className="text-green-600 font-medium">{envio.guia}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TablaScroll>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-center items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-blue-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                
                <span className="px-3 py-1 text-gray-600">
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-blue-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Estadísticas */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {historialFiltrado.length}
          </div>
          <div className="text-sm text-blue-700 font-medium">Total Registros</div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {historialFiltrado.filter(h => h.resultado === 'aprobado').length}
          </div>
          <div className="text-sm text-green-700 font-medium">Aprobados</div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">
            {historialFiltrado.filter(h => h.resultado === 'rechazado').length}
          </div>
          <div className="text-sm text-red-700 font-medium">Rechazados</div>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {historialFiltrado.reduce((sum, h) => sum + h.cantidad_despachada, 0)}
          </div>
          <div className="text-sm text-purple-700 font-medium">Total Tóners</div>
        </div>
      </div>
    </div>
  );
}
